import {PartyType} from '@sollapay/enums';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {buildRosterSidebarItem} from '@/components/roster';

import {summarizeBuyerEntry} from '../utils';

import type {BuyerRowAi} from './useBuyerFormAi';
import type {CreateBuyerFormValues} from '../validation';
import type {RosterSidebarItem} from '@/components/roster';
import type {FieldArrayWithId, UseFormReturn} from 'react-hook-form';

const PREFIX = 'view.buyers.addBuyerModal.buyerDetails';
const TYPE_PREFIX = 'view.buyers.addBuyerModal.buyerType';

/** Sidebar cards for the buyers roster — name, party type, live missing-field count and, while its own documents are read, an AI badge. */
export function useBuyerSidebarItems(
  form: UseFormReturn<CreateBuyerFormValues>,
  buyerFields: FieldArrayWithId<CreateBuyerFormValues, 'buyers'>[],
  rowAi: BuyerRowAi
): RosterSidebarItem[] {
  const {t} = useTranslation('trustAccounts');
  const {t: tCommon} = useTranslation('common');
  const buyers = useWatch({control: form.control, name: 'buyers'});

  return buyerFields.map((field, index) => {
    const entry = buyers[index];
    const summary = entry && summarizeBuyerEntry(entry);
    const isCorporate = entry?.partyType === PartyType.ORGANIZATION;
    // The row's key comes from the field array: the watched entry can lack `person` while extraction fills a row.
    const isExtracting = rowAi.processingScopes.has(field.person.buyerKey);

    return buildRosterSidebarItem({
      id: field.id,
      name: summary?.name,
      fallbackName: t(`${PREFIX}.newBuyer`),
      typeLabel: t(`${TYPE_PREFIX}.${isCorporate ? 'corporate' : 'individual'}`),
      issues: summary?.issues,
      issuesLabel: count => t(`${PREFIX}.issuesCount`, {count}),
      completeLabel: t(`${PREFIX}.complete`),
      extractingLabel: isExtracting ? tCommon('aiExtraction.field.generating') : undefined
    });
  });
}
